import React from 'react';
import { useAnnouncements } from './queries';
import Cookie from 'js-cookie';
import { Announcement as AnnouncementType } from './schemas';
import { Banner } from './Banner';
import { Dialog } from './Dialog';
import { Property } from 'shared-types/Property';

const COOKIE_NAME = 'announcementsDismissed';

type Props = {
  slug: Property['slug'];
  language?: string;
};

function getAnnouncementToDisplay(announcements: AnnouncementType[] = [], dismissed: string[]) {
  const firstDismissibleAnnouncement = announcements.find(
    (announcement) =>
      announcement?.content.type !== 'non_dismissible_banner' &&
      announcement?._id &&
      !dismissed.includes(announcement._id)
  );

  const firstNonDismissibleAnnouncement = announcements.find(
    (announcement) => announcement?.content.type === 'non_dismissible_banner'
  );

  return firstDismissibleAnnouncement || firstNonDismissibleAnnouncement || null;
}

export function Announcement({ slug, language }: Props) {
  const { data } = useAnnouncements(slug, language);
  const [visible, setVisible] = React.useState(true);
  const cookie = Cookie.get(COOKIE_NAME);
  let dismissed: string[] = [];

  if (cookie) {
    try {
      dismissed = JSON.parse(cookie);
    } catch {
      dismissed = [];
    }
  }

  const announcement = getAnnouncementToDisplay(data?.data, dismissed);

  const onDismiss = (id: string) => {
    setVisible(false);
    Cookie.set(COOKIE_NAME, JSON.stringify([...dismissed, id]), {
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'lax',
    });
  };

  if (announcement && visible) {
    return <Type {...{ announcement, onDismiss }} />;
  }

  return null;
}

function Type({
  announcement,
  onDismiss,
}: {
  announcement: AnnouncementType;
  onDismiss: (id: string) => void;
}) {
  const props = {
    heading: announcement.content.heading,
    body: announcement.content.body,
    dismissible: announcement.content.type !== 'non_dismissible_banner',
    onDismiss: () => onDismiss(announcement._id),
  };

  if (announcement.content.type === 'dialog') {
    return <Dialog {...props} />;
  }

  return <Banner {...props} />;
}
